import React from 'react';
import { show as showIntercom } from '@intercom/messenger-js-sdk';
import { isIntercomEnabled } from '../../analytics/adapters/intercomAdapter';
import styled from 'styled-components';
import { Breakpoints, Grid } from '../../../app/StyleGuide';
import { PrimaryButton } from '../../../components/Buttons';
import { Container } from '../../../components/Layout';
import WhiteLabel, { getWhiteLabel } from '../../../filters/WhiteLabel';
import windowService from '../../../services/windowService';
import { sanitizeHTML } from '../../../utils/sanitizeHTML';
import { ServiceUnavailableSVG as svg } from './StatusSVGs';

const CenteredContainer = styled(Container)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  max-width: 620px;
`;

const MainMessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${Grid._6};
  margin-bottom: ${Grid._7};

  > div {
    display: grid-block;
    grid-template-columns: 1fr;
    place-content: center;
  }

  h2 {
    margin-top: 0;
  }

  p:first-of-type {
    margin-bottom: ${Grid._1};
  }

  button {
    margin-top: ${Grid._3};
    width: 100%;
  }

  @media screen and (min-width: ${Breakpoints.screen_xs}) {
    flex-direction: row;

    button {
      width: auto;
    }
  }
`;

const Footer = styled.div`
  text-align: center;
`;

const Image = styled.object`
  width: ${Grid._14};
  height: 159px;
`;

const PhoneLink = () => {
  const gridWhiteLabel = getWhiteLabel('GRID');
  const gridPhoneNumber = gridWhiteLabel.contact?.phone || '';
  const gridFormattedPhoneNumber = gridPhoneNumber.replace(/-/g, '');
  return <a href={`tel:${gridFormattedPhoneNumber}`}>{gridPhoneNumber}</a>;
};

export const ErrorMessage = () => {
  const navigateToDashboard = () => {
    windowService.redirectTo('/dashboard');
  };

  const openIntercomChat = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    showIntercom();
  };

  return (
    <CenteredContainer data-qa-hook="error-message">
      <div>
        <MainMessageContainer>
          <div>
            <Image dangerouslySetInnerHTML={{ __html: sanitizeHTML(svg) }} />
          </div>
          <div>
            <h2>{WhiteLabel.name} encountered an issue</h2>
            <p>We&apos;re unable to display the contents of this page.</p>
            <p>Sorry for the inconvenience.</p>
            <PrimaryButton onClick={navigateToDashboard}>Return to Dashboard</PrimaryButton>
          </div>
        </MainMessageContainer>
        <Footer>
          {isIntercomEnabled() ? (
            <p>
              If this issue persists, use <a onClick={openIntercomChat}>Live Chat</a> to contact support or call us at{' '}
              <PhoneLink />
            </p>
          ) : (
            <p>
              If this issue persists, contact support at <PhoneLink />
            </p>
          )}
        </Footer>
      </div>
    </CenteredContainer>
  );
};
